import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle, AlertCircle, ArrowRight, Loader, Package, Info, CheckCircle, XCircle } from 'lucide-react';
import {
    PlanBatchMoveResponse,
    ExecuteBatchMoveResponse,
    HeatmapMoveReason,
    HeatmapMovePriority,
    HEATMAP_MOVE_REASON_LABELS,
    HEATMAP_MOVE_PRIORITY_LABELS,
    BatchMoveSource
} from './heatmapMoveTypes';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface SelectedLine {
    fromBinCode: string;
    itemNo: string;
    description1?: string;
    itemUnitCode?: string;
    totalQuantity: number;
    reservedQuantity: number;
    plannedQuantity: number;
    availableQuantity: number;
    selected: boolean;
    quantity: number;
}

interface HeatmapBatchMoveModalProps {
    isOpen: boolean;
    toBinCode: string;
    sourceBinCodes: string[];

    isLoadingPlan: boolean;
    plan: PlanBatchMoveResponse | null;
    planError: string | null;

    isExecuting: boolean;
    executeError: string | null;
    executeResult: ExecuteBatchMoveResponse | null;

    onClose: () => void;
    onConfirm: (params: {
        sources: BatchMoveSource[];
        reason: HeatmapMoveReason;
        priority: HeatmapMovePriority;
        comment: string;
    }) => Promise<void>;
    /** Optional: user chose "Legg i plan" instead of "Utfør nå". */
    onAddToPlan?: (sources: BatchMoveSource[]) => void;
}

/**
 * Modal for multi-source batch moves. Shows a flat list of all items
 * across all selected source bins. User can check/uncheck per line and
 * adjust quantities. Sum is validated against destination capacity.
 */
export const HeatmapBatchMoveModal: React.FC<HeatmapBatchMoveModalProps> = ({
    isOpen,
    toBinCode,
    sourceBinCodes,
    isLoadingPlan,
    plan,
    planError,
    isExecuting,
    executeError,
    executeResult,
    onClose,
    onConfirm,
    onAddToPlan
}) => {
    const { t } = useTranslation();
    const [lines, setLines] = useState<SelectedLine[]>([]);
    const [reason, setReason] = useState<HeatmapMoveReason>(HeatmapMoveReason.Reorganization);
    const [priority, setPriority] = useState<HeatmapMovePriority>(HeatmapMovePriority.Normal);
    const [comment, setComment] = useState('');

    // Flatten plan into selectable lines when it arrives.
    useEffect(() => {
        if (!plan) {
            setLines([]);
            return;
        }
        // Include ALL items (even fully locked ones with availableQuantity=0) so the
        // user can see why stock is unavailable (reserved / planned). Fully locked
        // rows are rendered disabled — they cannot be checked or moved.
        const flat: SelectedLine[] = [];
        for (const src of plan.sources) {
            for (const item of src.items) {
                const avail = item.availableQuantity;
                flat.push({
                    fromBinCode: src.fromBinCode,
                    itemNo: item.itemNo,
                    description1: item.description1,
                    itemUnitCode: item.itemUnitCode,
                    totalQuantity: item.totalQuantity,
                    reservedQuantity: item.reservedQuantity,
                    plannedQuantity: item.plannedQuantity || 0,
                    availableQuantity: avail,
                    selected: avail > 0,         // Only checked by default when movable
                    quantity: avail > 0 ? avail : 0
                });
            }
        }
        setLines(flat);
    }, [plan]);

    const destination = plan?.destinationInfo;

    const totals = useMemo(() => {
        const active = lines.filter(l => l.selected);
        const totalPlanned = lines.reduce((sum, l) => sum + (l.plannedQuantity || 0), 0);
        const totalReserved = lines.reduce((sum, l) => sum + (l.reservedQuantity || 0), 0);
        const linesWithPlanned = lines.filter(l => (l.plannedQuantity || 0) > 0).length;
        return {
            lineCount: active.length,
            totalQuantity: active.reduce((sum, l) => sum + l.quantity, 0),
            totalLines: lines.length,
            totalPlanned,
            totalReserved,
            linesWithPlanned,
            hasPlanned: totalPlanned > 0,
            hasReserved: totalReserved > 0
        };
    }, [lines]);

    // Capacity check: total selected qty vs destination space (null = unlimited).
    const availableSpace = destination?.availableSpace ?? Number.POSITIVE_INFINITY;
    const overCapacity = totals.totalQuantity > availableSpace;

    const canConfirm =
        !isLoadingPlan &&
        !isExecuting &&
        !executeResult &&
        plan?.canProceed === true &&
        totals.lineCount > 0 &&
        totals.totalQuantity > 0 &&
        !overCapacity &&
        lines.every(l => !l.selected || (l.quantity > 0 && l.quantity <= l.availableQuantity));

    const handleToggle = (idx: number) => {
        setLines(prev => prev.map((l, i) => {
            if (i !== idx) return l;
            // Fully locked rows (no availability) cannot be toggled on.
            if (l.availableQuantity <= 0) return l;
            return { ...l, selected: !l.selected };
        }));
    };

    const handleQuantityChange = (idx: number, value: number) => {
        setLines(prev => prev.map((l, i) => {
            if (i !== idx) return l;
            const clamped = isNaN(value) || value < 0 ? 0 : Math.min(value, l.availableQuantity);
            return { ...l, quantity: clamped };
        }));
    };

    const handleSelectAll = (select: boolean) => {
        // Skip fully locked rows when selecting all.
        setLines(prev => prev.map(l => l.availableQuantity > 0 ? { ...l, selected: select } : l));
    };

    const handleConfirm = async () => {
        if (!canConfirm) return;
        const sources: BatchMoveSource[] = lines
            .filter(l => l.selected && l.quantity > 0)
            .map(l => ({
                fromBinCode: l.fromBinCode,
                itemNo: l.itemNo,
                quantity: l.quantity
            }));
        await onConfirm({ sources, reason, priority, comment: comment.trim() });
    };

    // Detect single-source case (drag from ONE bin — all items in bin will be moved).
    const uniqueSources = Array.from(new Set(sourceBinCodes.map(s => s.toLowerCase())));
    const isSingleSource = uniqueSources.length === 1;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <Package className="w-6 h-6 text-purple-600" />
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {isSingleSource ? t('manager.modules.locationHeatmap.batchModal.titleSingle', {}, 'Flytt innhold fra lokasjon') : t('manager.modules.locationHeatmap.batchModal.titleMulti', {}, 'Samlet flytt')}
                            </h2>
                            <div className="text-sm text-gray-600 mt-0.5">
                                {isSingleSource ? (
                                    <>
                                        <span className="font-mono font-semibold">{sourceBinCodes[0]}</span>
                                        <ArrowRight className="w-4 h-4 inline mx-2" />
                                        <span className="font-mono font-semibold text-blue-700">{toBinCode}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="font-semibold">{t('manager.modules.locationHeatmap.batchModal.locationsCount', { '0': sourceBinCodes.length }, `${sourceBinCodes.length} lokasjoner`)}</span>
                                        <ArrowRight className="w-4 h-4 inline mx-2" />
                                        <span className="font-mono font-semibold text-blue-700">{toBinCode}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isExecuting}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        aria-label={t('common.buttons.close', {}, 'Lukk')}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {isLoadingPlan && (
                        <div className="flex items-center justify-center py-8">
                            <Loader className="w-6 h-6 animate-spin text-blue-600" />
                            <span className="ml-3 text-sm text-gray-600">{t('manager.modules.locationHeatmap.batchModal.loadingContent', {}, 'Laster innhold fra valgte lokasjoner...')}</span>
                        </div>
                    )}

                    {planError && !isLoadingPlan && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>{planError}</span>
                        </div>
                    )}

                    {/* Execution result view */}
                    {executeResult && (
                        <div className="space-y-3">
                            <div className={`border rounded-lg p-3 ${
                                executeResult.failedCount === 0
                                    ? 'bg-green-50 border-green-200 text-green-800'
                                    : executeResult.successCount === 0
                                      ? 'bg-red-50 border-red-200 text-red-800'
                                      : 'bg-amber-50 border-amber-200 text-amber-800'
                            }`}>
                                <div className="font-semibold text-sm mb-1">
                                    {executeResult.failedCount === 0
                                        ? t('manager.modules.locationHeatmap.result.allCreated', { '0': executeResult.successCount }, `✓ Alle ${executeResult.successCount} linjer opprettet`)
                                        : t('manager.modules.locationHeatmap.result.partialComplete', { '0': executeResult.successCount, '1': executeResult.failedCount }, `Delvis fullført: ${executeResult.successCount} lykkes, ${executeResult.failedCount} feilet`)}
                                </div>
                                {executeResult.stcOrderNo && (
                                    <div className="text-xs">{t('manager.modules.locationHeatmap.result.orderNoCreated', { '0': executeResult.stcOrderNo }, `Ordre #${executeResult.stcOrderNo} opprettet`)}</div>
                                )}
                            </div>

                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-2 py-1.5 text-left">{t('manager.modules.locationHeatmap.table.from', {}, 'Fra')}</th>
                                            <th className="px-2 py-1.5 text-left">{t('warehouse.general.item', {}, 'Vare')}</th>
                                            <th className="px-2 py-1.5 text-right">{t('manager.modules.locationHeatmap.table.amount', {}, 'Mengde')}</th>
                                            <th className="px-2 py-1.5 text-left">{t('common.fields.status', {}, 'Status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {executeResult.results.map((r, i) => (
                                            <tr key={i} className="border-t border-gray-100">
                                                <td className="px-2 py-1.5 font-mono">{r.fromBinCode}</td>
                                                <td className="px-2 py-1.5">{r.itemNo}</td>
                                                <td className="px-2 py-1.5 text-right">{r.quantity}</td>
                                                <td className="px-2 py-1.5">
                                                    {r.success ? (
                                                        <span className="flex items-center text-green-700">
                                                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                            {t('manager.modules.locationHeatmap.result.ok', {}, 'OK')}
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center text-red-700" title={r.errorMessage}>
                                                            <XCircle className="w-3.5 h-3.5 mr-1" />
                                                            {r.errorMessage || t('manager.modules.locationHeatmap.result.failed', {}, 'Feilet')}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Planning view */}
                    {!isLoadingPlan && plan && !executeResult && (
                        <div className="space-y-4">
                            {/* Errors */}
                            {plan.errors.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                                    {plan.errors.map((err, i) => (
                                        <div key={i} className="flex items-start space-x-2 text-sm text-red-800">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                            <span>{err.message}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Warnings */}
                            {plan.warnings.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                                    {plan.warnings.map((warn, i) => (
                                        <div key={i} className="flex items-start space-x-2 text-sm text-yellow-800">
                                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                            <span>{warn.message}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Destination info */}
                            {destination && (
                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900 space-y-1">
                                    <div className="flex items-center space-x-2 font-semibold">
                                        <Info className="w-4 h-4" />
                                        <span>{t('manager.modules.locationHeatmap.batchModal.destinationHeader', { '0': destination.binCode }, `Destinasjon ${destination.binCode} — hvor varene skal plasseres`)}</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        <div>{t('manager.modules.locationHeatmap.destination.type', {}, 'Type:')} <span className="font-mono">{destination.binTypeName || '-'}</span></div>
                                        <div>{t('manager.modules.locationHeatmap.destination.sharedShort', {}, 'Delt:')} <span className="font-semibold">{destination.sharedBinYesNo ? t('manager.modules.locationHeatmap.common.yes', {}, 'Ja') : t('manager.modules.locationHeatmap.common.no', {}, 'Nei')}</span></div>
                                        <div>{t('manager.modules.locationHeatmap.destination.balanceAtDestination', {}, 'Saldo i destinasjon:')} <span className="font-semibold">{destination.currentQuantity}</span></div>
                                        <div>{t('manager.modules.locationHeatmap.destination.availableSpace', {}, 'Ledig plass:')} <span className={`font-semibold ${overCapacity ? 'text-red-700' : ''}`}>
                                            {destination.availableSpace !== undefined && destination.availableSpace !== null
                                                ? destination.availableSpace
                                                : t('manager.modules.locationHeatmap.common.unlimited', {}, 'Ubegrenset')}
                                        </span></div>
                                    </div>
                                </div>
                            )}

                            {/* Items table */}
                            {(totals.hasPlanned || totals.hasReserved) && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start space-x-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">{t('manager.modules.locationHeatmap.batchModal.boundBalanceTitle', {}, 'Deler av saldoen er allerede bundet')}</div>
                                        <div className="mt-0.5 space-y-0.5">
                                            {totals.hasReserved && (
                                                <div>• <strong>{totals.totalReserved}</strong> {t('manager.modules.locationHeatmap.batchModal.reservedBullet', {}, 'stk er reservert (aktive reservasjoner)')}</div>
                                            )}
                                            {totals.hasPlanned && (
                                                <div>• <strong>{totals.totalPlanned}</strong> {totals.linesWithPlanned === 1
                                                    ? t('manager.modules.locationHeatmap.batchModal.plannedBulletSingular', { '0': totals.linesWithPlanned }, `stk er lagt opp i aktive flytteplaner (${totals.linesWithPlanned} vare)`)
                                                    : t('manager.modules.locationHeatmap.batchModal.plannedBulletPlural', { '0': totals.linesWithPlanned }, `stk er lagt opp i aktive flytteplaner (${totals.linesWithPlanned} varer)`)}</div>
                                            )}
                                            <div className="mt-0.5 text-amber-700">{t('manager.modules.locationHeatmap.batchModal.availableColumnNote', {}, 'Kolonnen "Tilgj." viser saldo etter at disse er trukket fra.')}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {lines.length > 0 ? (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-semibold text-gray-700">
                                            {isSingleSource
                                                ? t('manager.modules.locationHeatmap.batchModal.selectedItemsSummary', { '0': totals.lineCount, '1': totals.totalLines, '2': totals.totalQuantity }, `${totals.lineCount} av ${totals.totalLines} varer valgt · total ${totals.totalQuantity} stk`)
                                                : t('manager.modules.locationHeatmap.batchModal.selectedLinesSummary', { '0': totals.lineCount, '1': totals.totalLines, '2': totals.totalQuantity }, `${totals.lineCount} av ${totals.totalLines} linjer valgt · total ${totals.totalQuantity} stk`)}
                                        </div>
                                        <div className="space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAll(true)}
                                                className="text-xs text-blue-600 hover:underline"
                                            >
                                                {t('manager.modules.locationHeatmap.batchModal.selectAll', {}, 'Velg alle')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAll(false)}
                                                className="text-xs text-gray-600 hover:underline"
                                            >
                                                {t('manager.modules.locationHeatmap.batchModal.deselectAll', {}, 'Fjern alle')}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                                                <tr>
                                                    <th className="px-2 py-1.5 w-8"></th>
                                                    {!isSingleSource && <th className="px-2 py-1.5 text-left">{t('manager.modules.locationHeatmap.table.from', {}, 'Fra')}</th>}
                                                    <th className="px-2 py-1.5 text-left">{t('warehouse.general.item', {}, 'Vare')}</th>
                                                    {totals.hasPlanned && <th className="px-2 py-1.5 text-right">{t('manager.modules.locationHeatmap.table.planned', {}, 'Planlagt')}</th>}
                                                    <th className="px-2 py-1.5 text-right">{t('manager.modules.locationHeatmap.table.availableShort', {}, 'Tilgj.')}</th>
                                                    <th className="px-2 py-1.5 text-right w-24">{t('manager.modules.locationHeatmap.table.amount', {}, 'Mengde')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lines.map((line, idx) => {
                                                    const fullyLocked = line.availableQuantity <= 0;
                                                    const rowClass = fullyLocked
                                                        ? 'border-t border-gray-100 bg-gray-50 opacity-60'
                                                        : line.selected
                                                            ? 'border-t border-gray-100'
                                                            : 'border-t border-gray-100 opacity-50';
                                                    const lockTooltip = fullyLocked
                                                        ? `${line.plannedQuantity > 0 ? t('manager.modules.locationHeatmap.batchModal.tooltipPlanned', { '0': line.plannedQuantity }, `Planlagt: ${line.plannedQuantity} stk. `) : ''}${line.reservedQuantity > 0 ? t('manager.modules.locationHeatmap.batchModal.tooltipReserved', { '0': line.reservedQuantity }, `Reservert: ${line.reservedQuantity} stk. `) : ''}${t('manager.modules.locationHeatmap.batchModal.tooltipNoMovable', {}, 'Ingen flytt-bar saldo.')}`
                                                        : undefined;
                                                    return (
                                                    <tr key={`${line.fromBinCode}-${line.itemNo}`}
                                                        className={rowClass}
                                                        title={lockTooltip}>
                                                        <td className="px-2 py-1.5">
                                                            <input
                                                                type="checkbox"
                                                                checked={line.selected}
                                                                disabled={fullyLocked}
                                                                onChange={() => handleToggle(idx)}
                                                            />
                                                        </td>
                                                        {!isSingleSource && (
                                                            <td className="px-2 py-1.5 font-mono text-xs">{line.fromBinCode}</td>
                                                        )}
                                                        <td className="px-2 py-1.5 text-xs">
                                                            <div className="font-mono">{line.itemNo}</div>
                                                            {line.description1 && (
                                                                <div className="text-gray-500 truncate max-w-xs">{line.description1}</div>
                                                            )}
                                                        </td>
                                                        {totals.hasPlanned && (
                                                            <td className="px-2 py-1.5 text-right text-xs">
                                                                {line.plannedQuantity > 0 ? (
                                                                    <span className="text-amber-700 font-semibold" title={t('manager.modules.locationHeatmap.batchModal.plannedCellTooltip', {}, 'Allerede lagt opp i flytteplan(er) — derfor ikke tilgjengelig her')}>
                                                                        {line.plannedQuantity}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-300">0</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="px-2 py-1.5 text-right text-xs">
                                                            {line.availableQuantity} {line.itemUnitCode || ''}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-right">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={line.availableQuantity}
                                                                step="any"
                                                                value={line.quantity}
                                                                disabled={!line.selected || fullyLocked}
                                                                onChange={(e) => handleQuantityChange(idx, parseFloat(e.target.value))}
                                                                className="w-20 border border-gray-300 rounded px-2 py-0.5 text-xs text-right disabled:bg-gray-50"
                                                            />
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {overCapacity && destination?.availableSpace !== undefined && (
                                        <div className="mt-2 text-xs text-red-700 flex items-center space-x-1">
                                            <AlertCircle className="w-4 h-4" />
                                            <span>
                                                {t('manager.modules.locationHeatmap.batchModal.overCapacity', { '0': totals.totalQuantity, '1': destination.availableSpace }, `Valgt total (${totals.totalQuantity}) overstiger ledig plass (${destination.availableSpace}). Reduser mengden på en eller flere linjer.`)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
                                    {isSingleSource
                                        ? t('manager.modules.locationHeatmap.batchModal.emptySingle', { '0': sourceBinCodes[0] }, `Lokasjonen ${sourceBinCodes[0]} er tom — ingen varer registrert.`)
                                        : t('manager.modules.locationHeatmap.batchModal.emptyMulti', {}, 'Ingen varer registrert i de valgte lokasjonene.')}
                                </div>
                            )}

                            {/* Special case: bin HAS items but all of them are fully locked */}
                            {lines.length > 0 && totals.lineCount === 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start space-x-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">{t('manager.modules.locationHeatmap.batchModal.allLockedTitle', {}, 'All saldo er bundet — ingen varer kan flyttes nå')}</div>
                                        <div className="mt-0.5 text-xs">
                                            {t('manager.modules.locationHeatmap.batchModal.allLockedDescription', {}, 'Hele innholdet er lagt opp i andre flytteplaner eller reservert for plukk. Se tabellen under for hvilke varer som er berørt. Fjern plan-stegene eller vent til eksisterende flytter er utført før du flytter disse videre.')}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Reason */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.locationHeatmap.fields.reason', {}, 'Årsak')}</label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(parseInt(e.target.value, 10) as HeatmapMoveReason)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                >
                                    {Object.entries(HEATMAP_MOVE_REASON_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Priority */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">{t('common.fields.priority', {}, 'Prioritet')}</label>
                                <div className="flex space-x-2">
                                    {Object.entries(HEATMAP_MOVE_PRIORITY_LABELS).map(([value, label]) => {
                                        const v = parseInt(value, 10) as HeatmapMovePriority;
                                        const active = priority === v;
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setPriority(v)}
                                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                                                    active
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Comment */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    {t('manager.modules.locationHeatmap.fields.comment', {}, 'Kommentar')} <span className="text-gray-400 font-normal">{t('manager.modules.locationHeatmap.fields.optional', {}, '(valgfri)')}</span>
                                </label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={2}
                                    maxLength={200}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                                />
                            </div>

                            {executeError && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start space-x-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>{executeError}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-2 px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isExecuting}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-semibold"
                    >
                        {executeResult ? t('common.buttons.close', {}, 'Lukk') : t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    {!executeResult && onAddToPlan && (
                        <button
                            onClick={() => {
                                const sources: BatchMoveSource[] = lines
                                    .filter(l => l.selected && l.quantity > 0)
                                    .map(l => ({
                                        fromBinCode: l.fromBinCode,
                                        itemNo: l.itemNo,
                                        quantity: l.quantity
                                    }));
                                onAddToPlan(sources);
                            }}
                            disabled={!canConfirm}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            title={t('manager.modules.locationHeatmap.actions.addToPlanTooltip', {}, 'Legg til som steg i en flytteplan i stedet for å utføre nå')}
                        >
                            {t('manager.modules.locationHeatmap.actions.addToPlan', {}, 'Legg i plan')}
                        </button>
                    )}
                    {!executeResult && (
                        <button
                            onClick={handleConfirm}
                            disabled={!canConfirm}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center space-x-2"
                        >
                            {isExecuting ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    <span>{t('manager.modules.locationHeatmap.result.creating', {}, 'Oppretter...')}</span>
                                </>
                            ) : (
                                <span>{t('manager.modules.locationHeatmap.actions.executeNowCount', { '0': totals.lineCount }, `Utfør nå (${totals.lineCount})`)}</span>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
